import { useInkedBalance } from "./useInkedBalance";
import { useInkedUsage, Transaction } from "./useInkedUsage";
import { useInkedCampaignPreviewUrl } from "./useInkedCampaignPreviewUrl";
import Button from "../core/Button";
import { useState, useEffect } from "react";
import AddFundsDialog from "./AddFundsDialog";
import ViewSentCardsDialog from "./ViewSentCardsDialog";
import { Table, TableBody, TableRow, TableCell, TableHead, TableHeader } from "../core/Table";
import { TableCell as BaseTableCell } from "../shadcn";
import Pagination from "../SearchListings/Pagination";
import { ChevronDown, ChevronRight, ImageOff, Image, ChevronsDown, ChevronsRight, Loader2 } from "lucide-react";
import { useDebounce } from "../hookHelpers";
import SearchInput from "../core/SearchInput";
import { formatDateShort, formatFromNow, formatRelativeDay } from "src/utils/dates";

const PAGE_SIZE = 10;

const CardThumbnail = ({ src, alt }: { src: string; alt: string }) => {
  const [hasError, setHasError] = useState(false);

  if (hasError) {
    return (
      <div className="w-full h-full bg-gray-100 flex items-center justify-center">
        <ImageOff className="h-8 w-8 text-gray-400" />
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={alt}
      className="w-full h-full object-cover"
      onError={() => setHasError(true)}
    />
  );
};

interface TransactionRowProps {
  transaction: Transaction;
  isExpanded: boolean;
  onToggleExpand: () => void;
}

const TransactionRow = ({ transaction, isExpanded, onToggleExpand }: TransactionRowProps) => {
  const [isViewCardsOpen, setIsViewCardsOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const campaignPreviewMutation = useInkedCampaignPreviewUrl();
  // Only consider card sends that have meaningful recipient info to display
  // Exclude entries with just the default "Current Owner" name and no address
  const displayableCardSends = transaction.cardSends.filter(
    cs => cs.recipientAddress || (cs.recipientName && cs.recipientName !== "Current Owner")
  );
  const hasCardSends = displayableCardSends.length > 0;
  // Show first thumbnail as campaign preview
  const previewCard = transaction.cardSends[0];
  // Get thumbnail: transaction thumbnail, or first card send with a thumbnail
  const thumbnailUrl = transaction.thumbnailUrl ||
    transaction.cardSends.find(cs => cs.thumbnailUrl)?.thumbnailUrl || "";

  const handleViewCards = async () => {
    if (!previewCard) return;
    setIsViewCardsOpen(true);
    try {
      const url = await campaignPreviewMutation.mutateAsync(previewCard.campaignId);
      setPreviewUrl(url);
    } catch (error) {
      console.error("Failed to get campaign preview URL:", error);
    }
  };

  return (
    <>
      <TableRow
        className={`${hasCardSends ? "cursor-pointer hover:bg-gray-50" : ""} ${isExpanded ? "bg-gray-50 border-b-0" : ""}`}
        onClick={() => hasCardSends && onToggleExpand()}
      >
        <TableCell className="w-[40px] min-w-[40px] max-w-[40px] p-0 pl-4">
          {hasCardSends ? (
            <button
              className="p-1 hover:bg-gray-100 rounded"
              onClick={(e) => {
                e.stopPropagation();
                onToggleExpand();
              }}
            >
              {isExpanded ? (
                <ChevronDown className="h-4 w-4 text-gray-500" />
              ) : (
                <ChevronRight className="h-4 w-4 text-gray-500" />
              )}
            </button>
          ) : null}
        </TableCell>
        <TableCell className="w-20 pl-0">
          <div className="w-16 aspect-[1.414/1] lg:aspect-[594/420] border-2 rounded-lg shadow-lg overflow-hidden relative border-transparent hover:border-brand-100">
            {thumbnailUrl ? (
              <CardThumbnail src={thumbnailUrl} alt="Card preview" />
            ) : (
              <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                <Image className="h-8 w-8 text-gray-400" />
              </div>
            )}
          </div>
        </TableCell>
        <TableCell>
          <div>{formatDateShort(transaction.transactionDate)}</div>
          <div className="text-gray-secondary text-xs">{formatFromNow(transaction.transactionDate)}</div>
        </TableCell>
        <TableCell>
          <div>{formatDateShort(transaction.sendAt)}</div>
          <div className="text-gray-secondary text-xs">{formatRelativeDay(transaction.sendAt)}</div>
        </TableCell>
        <TableCell className="text-right">{transaction.itemCount}</TableCell>
        <TableCell className="text-right">${(transaction.amount / 100).toFixed(2)}</TableCell>
        <TableCell className="text-right">
          {previewCard && (
            <button
              className="text-primary text-sm hover:underline"
              onClick={(e) => {
                e.stopPropagation();
                handleViewCards();
              }}
            >
              View Cards
            </button>
          )}
        </TableCell>
      </TableRow>
      <ViewSentCardsDialog
        isOpen={isViewCardsOpen}
        onClose={() => {
          setIsViewCardsOpen(false);
          setPreviewUrl(null);
        }}
        previewUrl={previewUrl}
        isLoading={campaignPreviewMutation.isPending}
      />
      {isExpanded && hasCardSends && (
        <TableRow>
          <BaseTableCell colSpan={7} className="bg-gray-50 p-0">
            <div className="py-4 pl-[80px]">
              {displayableCardSends.map((cardSend) => (
                <div
                  key={cardSend.deliveryId}
                  className="flex items-center gap-4 py-2 border-b border-gray-200 last:border-b-0"
                >
                  <span className="text-gray-primary">{cardSend.recipientName}</span>
                  <span className="text-gray-secondary text-sm">{cardSend.recipientAddress}</span>
                </div>
              ))}
            </div>
          </BaseTableCell>
        </TableRow>
      )}
    </>
  );
};

interface SendCardsSettingsProps {
  showCardSendHistory?: boolean;
}

export const SendCardsSettings = ({ showCardSendHistory = false }: SendCardsSettingsProps) => {
  const { data: balanceData, isLoading: isLoadingBalance } = useInkedBalance();
  const [isAddFundsDialogOpen, setIsAddFundsDialogOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

  const { data: usageData, isLoading: isLoadingUsage } = useInkedUsage({
    limit: PAGE_SIZE,
    offset: (currentPage - 1) * PAGE_SIZE,
    search: debouncedSearchTerm,
  });

  // Auto-expand all rows when there's an active search
  useEffect(() => {
    if (debouncedSearchTerm.trim().length > 0 && usageData?.transactions) {
      const allIndices = usageData.transactions.map((_, index) => index);
      setExpandedRows(new Set(allIndices));
    } else if (debouncedSearchTerm.trim().length === 0) {
      setExpandedRows(new Set());
    }
  }, [debouncedSearchTerm, usageData?.transactions]);

  const balance = balanceData?.balance ?? 0;
  const cardPrice = balanceData?.cardPrice ?? 1;
  const cardsAvailable = cardPrice > 0 ? Math.floor(balance / cardPrice) : 0;

  const handleAddFundsComplete = () => {
    setIsAddFundsDialogOpen(false);
  };

  const toggleExpandRow = (index: number) => {
    const newExpandedRows = new Set(expandedRows);
    if (newExpandedRows.has(index)) {
      newExpandedRows.delete(index);
    } else {
      newExpandedRows.add(index);
    }
    setExpandedRows(newExpandedRows);
  };

  const toggleExpandAll = () => {
    if (expandedRows.size > 0) {
      setExpandedRows(new Set());
    } else {
      const allIndices = usageData?.transactions.map((_, index) => index) ?? [];
      setExpandedRows(new Set(allIndices));
    }
  };

  const areAllExpanded = usageData && expandedRows.size === usageData.transactions.length && usageData.transactions.length > 0;

  return (
    <section className="flex flex-col gap-20">
      <div className="w-[500px]">
        <div className="flex flex-col gap-4">
          <div className="flex justify-between items-center">
            <p className="text-gray-secondary">Funds</p>
            {isLoadingBalance ? (
              <div className="h-6 w-20 bg-gray-200 animate-pulse rounded" />
            ) : (
              <p className="text-gray-primary font-medium">${(balance / 100).toFixed(2)}</p>
            )}
          </div>
          <div className="flex justify-between items-center">
            <p className="text-gray-secondary">Price Per Card</p>
            {isLoadingBalance ? (
              <div className="h-6 w-20 bg-gray-200 animate-pulse rounded" />
            ) : (
              <p className="text-gray-primary font-medium">${(cardPrice / 100).toFixed(2)}</p>
            )}
          </div>
          <div className="flex justify-between items-center">
            <p className="text-gray-secondary">Cards Available</p>
            {isLoadingBalance ? (
              <div className="h-6 w-20 bg-gray-200 animate-pulse rounded" />
            ) : (
              <p className="text-gray-primary font-medium">{cardsAvailable.toLocaleString()}</p>
            )}
          </div>
          <div className="flex justify-between items-center">
            <p className="text-gray-secondary">Total Cards Sent</p>
            {isLoadingUsage ? (
              <div className="h-6 w-20 bg-gray-200 animate-pulse rounded" />
            ) : (
              <p className="text-gray-primary font-medium">{(usageData?.totalCardsSent ?? 0).toLocaleString()}</p>
            )}
          </div>
        </div>
        <div className="mt-6">
          <Button onClick={() => setIsAddFundsDialogOpen(true)}>
            Add Funds
          </Button>
        </div>
      </div>

      {showCardSendHistory && (
        <div>
          <div className="mb-4 flex items-start justify-between">
            <div>
              <h2 className="text-gray-primary font-semibold text-lg">Sent Cards</h2>
              <div className="h-5 mt-1">
                {isLoadingUsage ? (
                  <div className="h-5 w-48 bg-gray-200 animate-pulse rounded" />
                ) : (
                  <p className="text-gray-secondary text-sm">
                    Showing {((currentPage - 1) * PAGE_SIZE) + 1} to {Math.min(currentPage * PAGE_SIZE, usageData?.totalRecords ?? 0)} of {(usageData?.totalRecords ?? 0).toLocaleString()}
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {usageData && usageData.transactions.length > 0 && (
                <button
                  onClick={toggleExpandAll}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors"
                  title={areAllExpanded ? "Collapse all rows" : "Expand all rows"}
                >
                  {areAllExpanded ? (
                    <>
                      <ChevronsRight className="h-4 w-4" />
                      <span>Collapse All</span>
                    </>
                  ) : (
                    <>
                      <ChevronsDown className="h-4 w-4" />
                      <span>Expand All</span>
                    </>
                  )}
                </button>
              )}
              <SearchInput
                value={searchTerm}
                onChange={(value) => {
                  setSearchTerm(value);
                  setCurrentPage(1);
                  setExpandedRows(new Set());
                }}
                placeholder="Search by name or address..."
                className="w-64"
              />
            </div>
          </div>

          <div className="min-h-[400px]">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[40px] min-w-[40px] max-w-[40px] p-0 pl-4">{" "}</TableHead>
                  <TableHead className="w-20 pl-0">{" "}</TableHead>
                  <TableHead className="w-32">Created Date</TableHead>
                  <TableHead className="w-32">Send Date</TableHead>
                  <TableHead className="text-right w-32">Card Count</TableHead>
                  <TableHead className="w-20 text-right">Cost</TableHead>
                  <TableHead className="w-24 text-right">
                    {isLoadingUsage && <Loader2 className="h-4 w-4 animate-spin text-gray-400 inline-block" />}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {usageData?.transactions.length === 0 ? (
                  <TableRow>
                    <BaseTableCell colSpan={7} className="text-center py-8 text-gray-secondary">
                      {debouncedSearchTerm ? "No matching cards" : "No cards sent yet"}
                    </BaseTableCell>
                  </TableRow>
                ) : (
                  usageData?.transactions.map((transaction, index) => (
                    <TransactionRow
                      key={index}
                      transaction={transaction}
                      isExpanded={expandedRows.has(index)}
                      onToggleExpand={() => toggleExpandRow(index)}
                    />
                  ))
                )}
              </TableBody>
            </Table>

            {!isLoadingUsage && usageData && usageData.totalRecords > PAGE_SIZE && (
              <div className="mt-4 flex justify-end">
                <Pagination
                  numRecords={usageData.totalRecords}
                  pageSize={PAGE_SIZE}
                  currentPage={currentPage}
                  onPageChange={(page: number) => {
                    setCurrentPage(page);
                    setExpandedRows(new Set());
                  }}
                />
              </div>
            )}
          </div>
        </div>
      )}

      <AddFundsDialog
        isOpen={isAddFundsDialogOpen}
        onClose={() => setIsAddFundsDialogOpen(false)}
        onComplete={handleAddFundsComplete}
      />
    </section>
  );
};
